import { ExtensionBase } from "./extensionBase";
import { UiUtils } from "../common/uiUtils";
import { CommentViewerUtils } from "./comment/commentViewerUtils";
import { Misc } from "../common/misc";
import { viewerState } from "../viewer";
import { PositionClickTool } from "../tools/PositionClickTool";
import { ViewerUtils } from "../common/viewerUtils";
import { UnitConstants } from "../common/constants";

export class PositionExtension extends ExtensionBase {
    static extensionName = "PositionExtension";

    private subToolbar: Autodesk.Viewing.UI.ControlGroup;
    private positionButton: Autodesk.Viewing.UI.Button;

    private eventTool: PositionClickTool;

    private pickPosition = false;

    constructor(viewer: Autodesk.Viewing.GuiViewer3D, options: any) {
        super(viewer, options);
        // TODO: Use the CanvasEventTool with multiple names instead of different tool class
        this.eventTool = new PositionClickTool(this.viewer, this.onClickCanvas);
    }

    static registerExtension() {
        super.registerExtension(PositionExtension.extensionName, PositionExtension);
    }

    load(): boolean {
        console.log(`${PositionExtension.extensionName} is loaded!`);
        this.viewer.toolController.registerTool(this.eventTool);
        return super.load();
    };

    unload(): boolean {
        this.viewer.toolController.deregisterTool(this.eventTool);
        return super.unload();
    };

    onToolbarCreated(): void {
        this.positionButton = UiUtils.createButton({
            id: "SmartDrawings.PositionButton",
            tooltip: "Position",
            iconClass: "ion-md-contract",
            onClick: () => {
                if (!this.pickPosition) {
                    this.startPicking();
                } else {
                    this.stopPicking();
                }
            }
        });

        this.subToolbar = this.getControlGroup("SmartDrawings.Extensions");
        this.subToolbar.addControl(this.positionButton);
    }

    private startPicking() {
        if (!(this.viewer as any).getExtension("NewCommentExtension").isPanelVisible()) {
            this.pickPosition = true;
            this.viewer.toolController.activateTool("SmartDrawings.PositionClickTool");
            this.positionButton.setState(Autodesk.Viewing.UI.Button.State.ACTIVE);
            this.viewer.addEventListener(Autodesk.Viewing.TOOL_CHANGE_EVENT, this.onCommentViewerActivate);
        }
    }

    private stopPicking() {
        this.pickPosition = false;
        this.viewer.toolController.deactivateTool("SmartDrawings.PositionClickTool");
        this.positionButton.setState(Autodesk.Viewing.UI.Button.State.INACTIVE);
        this.viewer.removeEventListener(Autodesk.Viewing.TOOL_CHANGE_EVENT, this.onCommentViewerActivate);
    }

    private addPositionComment(pos: THREE.Vector3) {
        if (this.pickPosition) {
            const id = Misc.guid();
            // Convert position comment units (we use millimeters for now)
            let modelPos = pos.toModelUnits();
            const posText = `X=${modelPos.x.toFixed(0)}\nY=${modelPos.y.toFixed(0)}\nZ=${modelPos.z.toFixed(0)}`;
            const comments = { ...viewerState.comments };
            comments[id] = { id: id, text: posText, position: modelPos };
            viewerState.comments = comments;
            let commentViewerUtils = new CommentViewerUtils(this.viewer, () => { },
                commentId => (this.viewer as any).getExtension("NewCommentExtension").showComments(commentId));
            commentViewerUtils.addCommentMarker(id, pos, posText, true);
            commentViewerUtils = null;
        }
    }

    // Deactivate picking points if user opens comment tool
    onCommentViewerActivate = ({toolName, tool, active}) => {
        if (toolName === "SmartDrawings.CanvasEventTool" && active && this.pickPosition) {
            this.stopPicking();
        }
    }

    onClickCanvas = (x: number, y: number, tap: boolean) => {
        const rect = this.viewer.canvas.getBoundingClientRect();
        const xe = x - (tap ? 0 : rect.left);
        const ye = y - (tap ? 0 : rect.top);

        const hit = (this.viewer as any).clientToWorld(xe, ye);
        if (hit != null) {
            const { x, y, z } = hit.point;
            const pointPos = new THREE.Vector3(x, y, z);
            this.addPositionComment(pointPos);
        }
    }
}